import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../api";
import { fetchAccess } from "../access";
import HeadquartersPage from "../components/headquarters/HeadquartersPage";

const ROLES = [
  ["MANAGER", "Manager"],
  ["ASSISTANT", "Assistant"],
  ["ACCOUNTANT", "Accountant"],
  ["CONTRACTOR", "Contractor"],
  ["TRADE", "Trade"],
];

const PAY_TYPES = [
  ["", "Not set"],
  ["HOURLY", "Hourly"],
  ["SALARY", "Salary"],
  ["SUBCONTRACT", "Subcontract"],
];

const CODES = [
  ["NOTE", "Note"],
  ["CALL", "Call"],
  ["SITE", "Site"],
  ["ASSIGN", "Assign"],
  ["PAY", "Pay"],
  ["DIARY", "Diary"],
];

const emptyDraft = {
  name: "",
  role: "TRADE",
  trade: "",
  company: "",
  phone: "",
  email: "",
  city: "",
  license_number: "",
  pay_type: "",
  status: "ACTIVE",
  next_diary: "",
};

function labelOf(list, value) {
  return (list.find((item) => item[0] === value) || [value, value])[1];
}

function money(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function matchesQuery(row, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    row.name,
    row.employee_number,
    row.city,
    row.company,
    row.phone,
    row.email,
    row.trade,
    row.role,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [capsule, setCapsule] = useState("files");
  const [selectedId, setSelectedId] = useState(null);
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [adding, setAdding] = useState(true);
  const [note, setNote] = useState({ code: "NOTE", description: "", amount: "", next_diary: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState(null);
  const [logins, setLogins] = useState([]);
  const [accessEmail, setAccessEmail] = useState("");
  const [accessPassword, setAccessPassword] = useState("");
  const [accessLevel, setAccessLevel] = useState("assistant");
  const [accessMsg, setAccessMsg] = useState("");
  const searchRef = useRef(null);

  async function loadList() {
    const data = await apiRequest("/employees/");
    setRows(Array.isArray(data) ? data : []);
    return data;
  }

  async function openFile(id) {
    setErr("");
    setAdding(false);
    setSelectedId(id);
    setListOpen(false);
    const data = await apiRequest(`/employees/${id}`);
    setFile(data);
    setDraft({
      name: data.name || "",
      role: data.role || "TRADE",
      trade: data.trade || "",
      company: data.company || "",
      phone: data.phone || "",
      email: data.email || "",
      city: data.city || "",
      license_number: data.license_number || "",
      pay_type: data.pay_type || "",
      status: data.status || "ACTIVE",
      next_diary: data.next_diary || "",
    });
  }

  function startNew() {
    setAdding(true);
    setFile(null);
    setSelectedId(null);
    setDraft(emptyDraft);
    setListOpen(false);
  }

  useEffect(() => {
    fetchAccess().then(async (next) => {
      setAccess(next);
      if (next.access_level !== "owner") return;
      try {
        await loadList();
        setLogins(await apiRequest("/auth/users"));
      } catch (e) {
        setErr(e.message || "Could not load employees");
      }
    });
  }, []);

  useEffect(() => {
    function onDoc(event) {
      if (!searchRef.current?.contains(event.target)) setListOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const activeCount = rows.filter((row) => row.status === "ACTIVE").length;
  const diaryCount = rows.filter((row) => row.next_diary).length;
  const notes = file?.notes || [];

  const shown = useMemo(() => {
    return rows.filter((row) => {
      if (capsule === "active" && row.status !== "ACTIVE") return false;
      if (capsule === "diaries" && !row.next_diary) return false;
      return matchesQuery(row, query);
    });
  }, [rows, query, capsule]);

  const fileTitle = useMemo(() => {
    if (!file) return "No file open";
    return `${file.employee_number}  ${file.name}`;
  }, [file]);

  function setField(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function createEmployee(event) {
    event.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const created = await apiRequest("/employees/", {
        method: "POST",
        body: { ...draft, next_diary: draft.next_diary || null },
      });
      await loadList();
      await openFile(created.id);
    } catch (e) {
      setErr(e.message || "Could not add employee");
    } finally {
      setBusy(false);
    }
  }

  async function saveFile(event) {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      await apiRequest(`/employees/${file.id}`, {
        method: "PUT",
        body: { ...draft, next_diary: draft.next_diary || null },
      });
      await loadList();
      await openFile(file.id);
    } catch (e) {
      setErr(e.message || "Could not save file");
    } finally {
      setBusy(false);
    }
  }

  async function addNote(event) {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      await apiRequest(`/employees/${file.id}/notes`, {
        method: "POST",
        body: {
          code: note.code,
          description: note.description,
          amount: note.amount === "" ? null : note.amount,
          next_diary: note.next_diary || null,
        },
      });
      setNote({ code: "NOTE", description: "", amount: "", next_diary: "" });
      await loadList();
      await openFile(file.id);
    } catch (e) {
      setErr(e.message || "Could not add note");
    } finally {
      setBusy(false);
    }
  }

  async function giveAccess(event) {
    event.preventDefault();
    setAccessMsg("");
    try {
      const saved = await apiRequest("/auth/users", {
        method: "POST",
        body: { email: accessEmail, password: accessPassword, access_level: accessLevel },
      });
      setAccessMsg(`${saved.email} can sign in as ${saved.access_level}. Tell them the password yourself.`);
      setAccessPassword("");
      setLogins(await apiRequest("/auth/users"));
    } catch (e) {
      setAccessMsg(e.message || "Could not give access");
    }
  }

  async function setLoginPassword() {
    setAccessMsg("");
    try {
      const saved = await apiRequest("/auth/users/password", {
        method: "PUT",
        body: { email: accessEmail, password: accessPassword },
      });
      setAccessMsg(`Password updated for ${saved.email}.`);
      setAccessPassword("");
    } catch (e) {
      setAccessMsg(e.message || "Could not set password");
    }
  }

  async function setLoginLevel() {
    setAccessMsg("");
    try {
      const saved = await apiRequest("/auth/users/access", {
        method: "PUT",
        body: { email: accessEmail, access_level: accessLevel },
      });
      setAccessMsg(`${saved.email} is now ${saved.access_level}.`);
      setLogins(await apiRequest("/auth/users"));
    } catch (e) {
      setAccessMsg(e.message || "Could not change the level");
    }
  }

  async function removeAccess(email) {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    setAccessMsg("");
    try {
      await apiRequest(`/auth/users?email=${encodeURIComponent(email)}`, { method: "DELETE" });
      setAccessMsg(`${email} no longer has access.`);
      setLogins(await apiRequest("/auth/users"));
    } catch (e) {
      setAccessMsg(e.message || "Could not remove access");
    }
  }

  if (!access) {
    return <HeadquartersPage title="Employees" subtitle="Checking access." />;
  }

  if (access.access_level !== "owner") {
    return (
      <HeadquartersPage
        title="Employees"
        subtitle="Only the workspace owner can open this page."
      />
    );
  }

  return (
    <HeadquartersPage
      title="Employees"
      subtitle="One file at a time. Search pulls the matching people. The ledger under the file is the work."
    >
      <style>{`
        .jt-emp-capsules { display: flex; flex-wrap: wrap; gap: 8px; margin: 4px 0 14px; }
        .jt-emp-cap {
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.14);
          background: #10283d;
          color: #d7e7f5;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          line-height: 1.2;
        }
        .jt-emp-cap.on {
          background: #f5c542;
          color: #062033;
          border-color: #f5c542;
        }
        .jt-emp-stack { display: flex; flex-direction: column; gap: 12px; width: 100%; }
        .jt-emp-search { position: relative; width: 100%; }
        .jt-emp-search-row { display: flex; width: 100%; }
        .jt-emp-search input {
          flex: 1;
          min-width: 0;
          font-size: 16px;
          border-radius: 10px 0 0 10px;
        }
        .jt-emp-pull {
          width: 46px;
          border: 1px solid rgba(255,255,255,0.14);
          border-left: 0;
          border-radius: 0 10px 10px 0;
          background: #0c2236;
          color: #f5c542;
          font-size: 18px;
          cursor: pointer;
        }
        .jt-emp-drop {
          position: absolute;
          z-index: 20;
          left: 0;
          right: 0;
          top: calc(100% + 6px);
          background: #0c2236;
          border: 1px solid rgba(215,178,77,0.55);
          border-radius: 12px;
          max-height: 50vh;
          overflow: auto;
          box-shadow: 0 12px 30px rgba(0,0,0,0.35);
        }
        .jt-emp-hit {
          width: 100%;
          text-align: left;
          background: transparent;
          color: #ffffff;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          padding: 12px 14px;
          cursor: pointer;
        }
        .jt-emp-hit strong { display: block; }
        .jt-emp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
        .jt-workforce-table th, .jt-workforce-table td {
          text-align: left;
          padding: 8px 6px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          vertical-align: top;
        }
        .jt-workforce-table th {
          color: #d7b24d;
          font-size: 11px;
          letter-spacing: 0.4px;
          text-transform: uppercase;
        }
        @media (max-width: 700px) {
          .jt-emp-grid { grid-template-columns: 1fr; }
          .jt-emp-cap { min-height: 36px; }
        }
      `}</style>

      {access.access_level === "owner" && (
        <section style={{ ...sheet, marginBottom: 14 }}>
          <div style={kicker}>App access</div>
          <p style={{ ...muted, marginBottom: 10 }}>
            You set the email and password here, then tell the person yourself. No invite is sent. Assistant can create estimates and invoices and accept payments, but cannot send email. Clerk can do the desk work, including drafts, but cannot take payments or send email.
          </p>
          {logins.map((row) => (
            <div key={row.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ color: "#e8f1f8", fontSize: 14 }}>{row.email} · {row.access_level}</span>
              {row.email !== undefined && (
                <button type="button" onClick={() => removeAccess(row.email)} style={{ ...goldButton, background: "transparent", color: "#ffb4b4", border: "1px solid rgba(255,180,180,0.5)", padding: "6px 10px" }}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <form onSubmit={giveAccess} className="jt-emp-grid" style={{ marginTop: 10 }}>
            <Field label="Email">
              <input type="email" value={accessEmail} onChange={(e) => setAccessEmail(e.target.value)} style={field} required />
            </Field>
            <Field label="Password">
              <input type="password" value={accessPassword} onChange={(e) => setAccessPassword(e.target.value)} style={field} minLength={8} required />
            </Field>
            <Field label="Level">
              <select value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)} style={field}>
                <option value="assistant">Assistant</option>
                <option value="clerk">Clerk</option>
                <option value="owner">Owner</option>
              </select>
            </Field>
            <div style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
              <button type="submit" style={goldButton}>Give access</button>
              <button type="button" onClick={setLoginLevel} style={{ ...goldButton, background: "#d7b24d" }}>Set level</button>
              <button type="button" onClick={setLoginPassword} style={{ ...goldButton, background: "#00c2ff" }}>Set password</button>
            </div>
          </form>
          {accessMsg && <div style={{ ...muted, marginTop: 8 }}>{accessMsg}</div>}
        </section>
      )}

      <div className="jt-emp-capsules">
        <button type="button" className={capsule === "active" ? "jt-emp-cap on" : "jt-emp-cap"} onClick={() => { setCapsule("active"); setListOpen(true); }}>
          Active {activeCount}
        </button>
        <button type="button" className={capsule === "files" ? "jt-emp-cap on" : "jt-emp-cap"} onClick={() => { setCapsule("files"); setListOpen(true); }}>
          Files {rows.length}
        </button>
        <button type="button" className={capsule === "diaries" ? "jt-emp-cap on" : "jt-emp-cap"} onClick={() => { setCapsule("diaries"); setListOpen(true); }}>
          Diaries {diaryCount}
        </button>
      </div>

      {err && <div style={errorBox}>{err}</div>}

      <div className="jt-emp-stack">
        <button type="button" onClick={startNew} style={{ ...goldButton, width: "100%" }}>
          Add employee
        </button>

        <div className="jt-emp-search" ref={searchRef}>
          <div className="jt-emp-search-row">
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setListOpen(true); }}
              onFocus={() => setListOpen(true)}
              placeholder="Name, employee number, or city"
              style={field}
            />
            <button type="button" className="jt-emp-pull" aria-label="Show employees" onClick={() => setListOpen((open) => !open)}>
              ▾
            </button>
          </div>
          {listOpen && (
            <div className="jt-emp-drop">
              {shown.map((row) => (
                <button key={row.id} type="button" className="jt-emp-hit" onClick={() => openFile(row.id).catch((e) => setErr(e.message || "Could not open file"))}>
                  <strong>{row.name}</strong>
                  <span style={muted}>
                    {row.employee_number} · {labelOf(ROLES, row.role)}
                    {row.trade ? ` · ${row.trade}` : ""}
                    {row.city ? ` · ${row.city}` : ""}
                  </span>
                </button>
              ))}
              {shown.length === 0 && <div style={{ ...muted, padding: 14 }}>No matching employees.</div>}
            </div>
          )}
        </div>

        <section style={sheet}>
          <div style={sheetHead}>
            <div>
              <div style={kicker}>{adding ? "New file" : "File open"}</div>
              <div style={sheetTitle}>{adding ? "Add employee" : fileTitle}</div>
            </div>
            <div style={statusPill}>{adding ? "NEW" : draft.status || "ACTIVE"}</div>
          </div>

          <form onSubmit={adding ? createEmployee : saveFile}>
            <div className="jt-emp-grid">
              <Field label="Name">
                <input value={draft.name} onChange={(e) => setField("name", e.target.value)} style={field} required />
              </Field>
              <Field label="Role">
                <select value={draft.role} onChange={(e) => setField("role", e.target.value)} style={field}>
                  {ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Trade">
                <input value={draft.trade} onChange={(e) => setField("trade", e.target.value)} placeholder="Carpenter, concrete, electrician" style={field} />
              </Field>
              <Field label="Company">
                <input value={draft.company} onChange={(e) => setField("company", e.target.value)} style={field} />
              </Field>
              <Field label="Phone">
                <input value={draft.phone} onChange={(e) => setField("phone", e.target.value)} style={field} />
              </Field>
              <Field label="Email">
                <input value={draft.email} onChange={(e) => setField("email", e.target.value)} style={field} />
              </Field>
              <Field label="City">
                <input value={draft.city} onChange={(e) => setField("city", e.target.value)} style={field} />
              </Field>
              <Field label="License">
                <input value={draft.license_number} onChange={(e) => setField("license_number", e.target.value)} style={field} />
              </Field>
              <Field label="Pay">
                <select value={draft.pay_type} onChange={(e) => setField("pay_type", e.target.value)} style={field}>
                  {PAY_TYPES.map(([value, label]) => <option key={value || "none"} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={draft.status} onChange={(e) => setField("status", e.target.value)} style={field}>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </Field>
              <Field label="Next diary">
                <input type="date" value={draft.next_diary || ""} onChange={(e) => setField("next_diary", e.target.value)} style={field} />
              </Field>
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="submit" disabled={busy} style={{ ...goldButton, width: "100%" }}>
                {adding ? "Create file" : "Save file"}
              </button>
            </div>
          </form>

          {file && (
            <>
              <form onSubmit={addNote} style={{ marginTop: 18 }}>
                <div style={kicker}>Add to the ledger</div>
                <div className="jt-emp-grid" style={{ marginTop: 8 }}>
                  <Field label="Code">
                    <select value={note.code} onChange={(e) => setNote({ ...note, code: e.target.value })} style={field}>
                      {CODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </Field>
                  <Field label="Amount">
                    <input value={note.amount} onChange={(e) => setNote({ ...note, amount: e.target.value })} placeholder="Optional" style={field} />
                  </Field>
                  <Field label="Move diary">
                    <input type="date" value={note.next_diary} onChange={(e) => setNote({ ...note, next_diary: e.target.value })} style={field} />
                  </Field>
                </div>
                <textarea
                  value={note.description}
                  onChange={(e) => setNote({ ...note, description: e.target.value })}
                  placeholder="What happened on this file"
                  required
                  style={{ ...field, marginTop: 8, minHeight: 70 }}
                />
                <button type="submit" disabled={busy} style={{ ...goldButton, marginTop: 8, width: "100%" }}>Add note</button>
              </form>

              <div style={{ overflowX: "auto", marginTop: 16 }}>
                <table className="jt-workforce-table" style={table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((item) => (
                      <tr key={item.id}>
                        <td>{item.note_date || "—"}</td>
                        <td>{item.user_label || "—"}</td>
                        <td>{labelOf(CODES, item.code)}</td>
                        <td>{item.description}</td>
                        <td>{money(item.amount) || "—"}</td>
                      </tr>
                    ))}
                    {notes.length === 0 && (
                      <tr>
                        <td colSpan={5} style={muted}>No ledger lines yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </HeadquartersPage>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </label>
  );
}

const sheet = {
  background: "#13293d",
  border: "1px solid rgba(215,178,77,0.45)",
  borderRadius: 12,
  padding: 16,
  width: "100%",
  boxSizing: "border-box",
};

const sheetHead = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  marginBottom: 14,
};

const sheetTitle = { color: "#ffffff", fontWeight: 900, fontSize: 22 };
const kicker = { color: "#d7b24d", fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" };
const muted = { color: "#9fb0c3", fontSize: 12, marginTop: 2 };
const labelStyle = { color: "#9fb0c3", fontSize: 12, marginBottom: 4 };

const field = {
  width: "100%",
  boxSizing: "border-box",
  background: "#0c2236",
  color: "#ffffff",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  padding: "10px 12px",
};

const goldButton = {
  background: "#f5c542",
  color: "#062033",
  border: 0,
  borderRadius: 10,
  padding: "12px 14px",
  fontWeight: 900,
  cursor: "pointer",
};

const statusPill = {
  background: "#0c2740",
  color: "#f5c542",
  border: "1px solid #d7b24d",
  borderRadius: 999,
  padding: "6px 10px",
  fontWeight: 800,
  fontSize: 12,
};

const table = { width: "100%", borderCollapse: "collapse", color: "#e8f1f8", fontSize: 13 };
const errorBox = {
  marginBottom: 12,
  padding: 12,
  borderRadius: 10,
  background: "rgba(255,80,80,0.12)",
  color: "#ffb4b4",
};
